import React, { useState, useRef } from 'react';
import { Camera, Upload, X, CheckCircle, AlertTriangle } from 'lucide-react';
import { uploadProfilePicture, uploadPetPicture } from '../services/storageService';

const ProfilePictureUpload = ({ 
  currentAvatar, 
  onUploadComplete, 
  size = 'medium',
  label = 'Profile Picture',
  userId,
  petId = null // If provided, uploads as pet picture
}) => {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const sizeClasses = {
    small: 'w-16 h-16',
    medium: 'w-24 h-24',
    large: 'w-32 h-32'
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPG, PNG, or WebP)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image is too large. Please choose a photo under 10MB.');
      return;
    }

    setError('');
    setUploadSuccess(false);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to Firebase
    await handleUpload(file);
  };

  const handleUpload = async (file) => {
    setUploading(true);
    
    try {
      let result;
      
      if (petId) {
        // Upload as pet picture
        result = await uploadPetPicture(userId, petId, file);
      } else {
        // Upload as profile picture
        result = await uploadProfilePicture(userId, file);
      }

      if (result.success) {
        onUploadComplete(result.url);
        setUploadSuccess(true);
        setPreview(null);
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePreview = () => {
    setPreview(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const displayImage = preview || currentAvatar;

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      
      <div className="flex items-center gap-6">
        {/* Avatar Display */}
        <div className="relative group">
          <img
            src={displayImage}
            alt="Profile"
            className={`${sizeClasses[size]} rounded-xl object-cover border-2 border-gray-200 ${
              uploading ? 'opacity-50' : ''
            }`}
          />
          
          {/* Hover Overlay */}
          <button
            onClick={handleClick}
            disabled={uploading}
            className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl cursor-pointer disabled:cursor-not-allowed"
          >
            {uploading ? (
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-white border-t-transparent" />
            ) : (
              <Camera className="w-8 h-8 text-white" />
            )}
          </button>

          {/* Upload Status Badge */}
          {uploading && (
            <div className="absolute -top-2 -right-2 bg-blue-600 text-white rounded-full p-1.5">
              <Upload className="w-4 h-4 animate-pulse" />
            </div>
          )}
        </div>

        {/* Upload Button & Info */}
        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <button
            onClick={handleClick}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-4 h-4" />
            {uploading ? 'Uploading...' : 'Change Photo'}
          </button>
          
          <div className="mt-2 text-xs text-gray-500 space-y-0.5">
            <p className="font-medium">Requirements:</p>
            <p>• Square image, 400×400px minimum</p>
            <p>• JPG, PNG, or WebP format</p>
            <p>• Photos are compressed automatically before upload</p>
          </div>

          {/* Preview Controls */}
          {preview && !uploading && (
            <button
              onClick={handleRemovePreview}
              className="mt-2 text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Cancel upload
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {uploadSuccess && !uploading && !error && (
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle className="w-4 h-4" />
          <p className="text-sm">Profile picture updated successfully</p>
        </div>
      )}
    </div>
  );
};

export default ProfilePictureUpload;
